import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, ArrowLeft, FileText, Building, Shield, CheckCircle } from "lucide-react";
import { api } from "@/lib/api";
import Header from "@/components/marketing/Header";
import Footer from "@/components/marketing/Footer";

export default function NonprofitPage() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    organization_name: "", ein_number: "", organization_type: "501c3",
    mission_statement: "", contact_name: "", contact_email: "", contact_phone: "",
    address: "", city: "", state: "", zip_code: "", website: "",
    annual_budget: "", tax_exempt_letter_url: "", agrees_to_terms: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    const token = localStorage.getItem("creator_token");
    if (!token) {
      setError("You must register as a creator first before applying for non-profit status.");
      return;
    }
    
    if (!form.agrees_to_terms) {
      setError("You must agree to the terms and conditions.");
      return;
    }

    setLoading(true);
    try {
      await api.applyNonprofit(form, token);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#4D00FF]/50 transition-all text-sm";

  return (
    <div className="min-h-screen bg-[#05050A]" data-testid="nonprofit-page">
      <Header />
      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-2xl bg-[#4D00FF]/10 flex items-center justify-center">
                <Heart className="w-6 h-6 text-[#4D00FF]" />
              </div>
              <div>
                <p className="label-mono" style={{ color: "#4D00FF" }}>Special Program</p>
                <h1 className="font-['Unbounded'] text-3xl sm:text-4xl font-black tracking-tighter text-white">
                  Non-Profit Program
                </h1>
              </div>
            </div>
            <p className="text-[#A0A0AB] text-base max-w-2xl mt-4">
              Fortis Digital supports registered non-profit organizations with special pricing.
              Only <span className="text-[#4D00FF] font-bold">$4.99/month</span> with just <span className="text-[#4D00FF] font-bold">10% commission</span> on sales.
              Tax-exempt verification is required.
            </p>
          </motion.div>

          {/* Benefits */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
            {[
              { icon: Building, title: "Unlimited Listings", desc: "List all your products without limits" },
              { icon: Shield, title: "10% Commission", desc: "Lowest commission rate available" },
              { icon: FileText, title: "Non-Profit Badge", desc: "Verified badge on all your listings" },
            ].map((b, i) => (
              <motion.div key={b.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                className="glass-card rounded-2xl p-5">
                <b.icon className="w-6 h-6 text-[#4D00FF] mb-3" />
                <h3 className="text-white font-semibold text-sm mb-1">{b.title}</h3>
                <p className="text-[#A0A0AB] text-xs">{b.desc}</p>
              </motion.div>
            ))}
          </div>

          {submitted ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="glass-card rounded-3xl p-10 text-center">
              <CheckCircle className="w-16 h-16 text-[#4D00FF] mx-auto mb-6" />
              <h2 className="font-['Unbounded'] text-2xl font-bold text-white mb-3">Application Submitted</h2>
              <p className="text-[#A0A0AB] text-base mb-6">
                Thank you! We'll review your tax-exempt documentation and get back to you within 2-3 business days.
              </p>
              <Link to="/creator/dashboard" className="neon-btn text-sm py-2 px-6">Go to Dashboard</Link>
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="glass-card rounded-3xl p-8">
                <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-2">Tax-Exempt Application</h2>
                <p className="text-[#A0A0AB] text-sm mb-8">Complete all fields. This information is required for tax and legal compliance.</p>

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="nonprofit-form">
                  {/* Organization Info */}
                  <div>
                    <h3 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider flex items-center gap-2">
                      <Building className="w-4 h-4 text-[#4D00FF]" /> Organization Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-[#A0A0AB] mb-1 block">Organization Legal Name *</label>
                        <input data-testid="np-org-name" className={inputClass} value={form.organization_name} onChange={(e) => setForm({...form, organization_name: e.target.value})} required placeholder="Organization name" />
                      </div>
                      <div>
                        <label className="text-xs text-[#A0A0AB] mb-1 block">EIN / Tax ID Number *</label>
                        <input data-testid="np-ein" className={inputClass} value={form.ein_number} onChange={(e) => setForm({...form, ein_number: e.target.value})} required placeholder="XX-XXXXXXX" />
                      </div>
                      <div>
                        <label className="text-xs text-[#A0A0AB] mb-1 block">Organization Type *</label>
                        <select data-testid="np-org-type" className={inputClass} value={form.organization_type} onChange={(e) => setForm({...form, organization_type: e.target.value})}>
                          <option value="501c3">501(c)(3) - Charitable</option>
                          <option value="501c4">501(c)(4) - Social Welfare</option>
                          <option value="501c6">501(c)(6) - Business League</option>
                          <option value="501c7">501(c)(7) - Social Club</option>
                          <option value="other">Other Tax-Exempt</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-[#A0A0AB] mb-1 block">Annual Budget</label>
                        <select className={inputClass} value={form.annual_budget} onChange={(e) => setForm({...form, annual_budget: e.target.value})}>
                          <option value="">Select range</option>
                          <option value="under_50k">Under $50,000</option>
                          <option value="50k_250k">$50,000 - $250,000</option>
                          <option value="250k_1m">$250,000 - $1,000,000</option>
                          <option value="over_1m">Over $1,000,000</option>
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-xs text-[#A0A0AB] mb-1 block">Mission Statement *</label>
                        <textarea data-testid="np-mission" className={inputClass + " resize-none"} rows={3} value={form.mission_statement} onChange={(e) => setForm({...form, mission_statement: e.target.value})} required placeholder="Describe your organization's mission..." />
                      </div>
                      <div>
                        <label className="text-xs text-[#A0A0AB] mb-1 block">Website</label>
                        <input className={inputClass} value={form.website} onChange={(e) => setForm({...form, website: e.target.value})} placeholder="https://..." />
                      </div>
                      <div>
                        <label className="text-xs text-[#A0A0AB] mb-1 block">IRS Determination Letter URL</label>
                        <input data-testid="np-letter-url" className={inputClass} value={form.tax_exempt_letter_url} onChange={(e) => setForm({...form, tax_exempt_letter_url: e.target.value})} placeholder="Link to your IRS determination letter" />
                      </div>
                    </div>
                  </div>

                  {/* Contact */}
                  <div>
                    <h3 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#4D00FF]" /> Contact Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs text-[#A0A0AB] mb-1 block">Contact Name *</label>
                        <input data-testid="np-contact-name" className={inputClass} value={form.contact_name} onChange={(e) => setForm({...form, contact_name: e.target.value})} required placeholder="Full name" />
                      </div>
                      <div>
                        <label className="text-xs text-[#A0A0AB] mb-1 block">Contact Email *</label>
                        <input data-testid="np-contact-email" type="email" className={inputClass} value={form.contact_email} onChange={(e) => setForm({...form, contact_email: e.target.value})} required placeholder="email@org.com" />
                      </div>
                      <div>
                        <label className="text-xs text-[#A0A0AB] mb-1 block">Phone</label>
                        <input className={inputClass} value={form.contact_phone} onChange={(e) => setForm({...form, contact_phone: e.target.value})} placeholder="(555) 123-4567" />
                      </div>
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <h3 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider flex items-center gap-2">
                      <Shield className="w-4 h-4 text-[#4D00FF]" /> Organization Address
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <label className="text-xs text-[#A0A0AB] mb-1 block">Street Address</label>
                        <input className={inputClass} value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} placeholder="123 Main St" />
                      </div>
                      <div>
                        <label className="text-xs text-[#A0A0AB] mb-1 block">City</label>
                        <input className={inputClass} value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} placeholder="City" />
                      </div>
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <label className="text-xs text-[#A0A0AB] mb-1 block">State</label>
                          <input className={inputClass} value={form.state} onChange={(e) => setForm({...form, state: e.target.value})} placeholder="ST" />
                        </div>
                        <div className="flex-1">
                          <label className="text-xs text-[#A0A0AB] mb-1 block">ZIP Code</label>
                          <input className={inputClass} value={form.zip_code} onChange={(e) => setForm({...form, zip_code: e.target.value})} placeholder="12345" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Terms */}
                  <div className="glass-card rounded-2xl p-5 border-[#4D00FF]/20">
                    <h3 className="text-sm font-semibold text-white mb-3">Legal Agreement</h3>
                    <div className="text-[#A0A0AB] text-xs space-y-2 mb-4">
                      <p>By submitting this application, I certify that:</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>The organization named above is a registered non-profit entity under applicable law.</li>
                        <li>The EIN/Tax ID provided is accurate and belongs to this organization.</li>
                        <li>The organization holds a valid tax-exempt determination letter from the IRS.</li>
                        <li>I am authorized to represent this organization and submit this application.</li>
                        <li>All information provided is true, complete, and accurate to the best of my knowledge.</li>
                        <li>I understand that providing false information may result in termination of the non-profit account and legal action.</li>
                        <li>I agree to Fortis Digital LLC's Terms of Service and Non-Profit Program guidelines.</li>
                      </ul>
                    </div>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        data-testid="np-agree-terms"
                        type="checkbox"
                        checked={form.agrees_to_terms}
                        onChange={(e) => setForm({...form, agrees_to_terms: e.target.checked})}
                        className="accent-[#4D00FF] w-4 h-4"
                      />
                      <span className="text-white text-sm">I agree to all terms and certify the above statements are true</span>
                    </label>
                  </div>

                  {error && <p className="text-red-400 text-sm" data-testid="np-error">{error}</p>}

                  <button
                    data-testid="np-submit"
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 rounded-full text-sm font-semibold transition-all bg-[#4D00FF] text-white hover:shadow-[0_0_25px_rgba(77,0,255,0.6)] disabled:opacity-50"
                  >
                    {loading ? "Submitting..." : "Submit Non-Profit Application"}
                  </button>
                </form>
              </div>
            </motion.div>
          )}

          <p className="text-[#A0A0AB] text-xs text-center mt-8">
            Don't have a creator account yet? <Link to="/creator" className="text-[#4D00FF] hover:underline">Register first</Link>, then come back to apply.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
